/**
 * Data Dragon utilities
 * Uses JSON files from Riot's Data Dragon CDN
 */

// Import DDragon JSON files (user will provide these in the data folder)
import championData from "../data/champion.json";
import runesData from "../data/runesReforged.json";
import summonerData from "../data/summoner.json";

import bronzeImg from "@/assets/ranks/BRONZE.png";
import challengerImg from "@/assets/ranks/CHALLENGER.png";
import diamondImg from "@/assets/ranks/DIAMOND.png";
import emeraldImg from "@/assets/ranks/EMERALD.png";
import goldImg from "@/assets/ranks/GOLD.png";
import grandmasterImg from "@/assets/ranks/GRANDMASTER.png";
import ironImg from "@/assets/ranks/IRON.png";
import masterImg from "@/assets/ranks/MASTER.png";
import platinumImg from "@/assets/ranks/PLATINUM.png";
import silverImg from "@/assets/ranks/SILVER.png";
import unrankedImg from "@/assets/ranks/UNRANKED.png";
import { TIER_COLORS } from "@/types";

// 1x1 transparent PNG: shown when an id is newer than the bundled Data Dragon files
const PLACEHOLDER_ICON = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

// Art is served through our own Worker (/dd, long-lived cache headers; Riot's CDN sends none, so every icon
// was revalidated on every page view). Version is pinned to the bundled JSON, which fetch-data refreshes on each build.
// ponytail: bundled names/ids only refresh on deploy; a champ released mid-patch shows a placeholder until the next build.
// The local dev server has no /dd route, so it talks to Riot directly.
const DD_BASE = typeof location !== "undefined" && location.hostname === "localhost" ? "https://ddragon.leagueoflegends.com/cdn" : "/dd";
const DD_VERSION = (championData as { version?: string }).version || "16.3.1";
const DD_CDN = `${DD_BASE}/${DD_VERSION}`;

/** Riot build ("16.1.612.4890") -> the Data Dragon version of that patch ("16.1.1"). No / malformed build -> current patch. */
export function ddVersion(gameVersion?: string): string {
	const m = /^(\d+\.\d+)\./.exec(gameVersion ?? "");
	return m ? `${m[1]}.1` : DD_VERSION;
}

const cdn = (gameVersion?: string) => `${DD_BASE}/${ddVersion(gameVersion)}`;

/** <img onError>: a game on a patch Data Dragon has not published yet (first hours of a patch) falls back to current art, once. */
export function ddFallback(e: { currentTarget: HTMLImageElement }) {
	const img = e.currentTarget;
	const current = img.src.replace(/\/\d+\.\d+\.\d+\/img\//, `/${DD_VERSION}/img/`);
	if (current !== img.src) img.src = current;
}

// Types for DDragon data structures
interface SummonerSpellData {
	data: Record<
		string,
		{
			id: string;
			key: string;
			name: string;
			image: { full: string };
		}
	>;
}

interface RuneData {
	id: number;
	key: string;
	icon: string;
	name: string;
}

interface RuneTreeData {
	id: number;
	key: string;
	icon: string;
	name: string;
	slots: Array<{
		runes: RuneData[];
	}>;
}

interface ChampionData {
	data: Record<
		string,
		{
			id: string;
			key: string;
			name: string;
			image: { full: string };
		}
	>;
}

const spells = summonerData as SummonerSpellData;
const runes = runesData as RuneTreeData[];
const champions = championData as ChampionData;

// Build lookup maps for efficient access
const spellByKey = new Map<string, { id: string; name: string; image: string }>();
for (const spell of Object.values(spells.data)) {
	spellByKey.set(spell.key, {
		id: spell.id,
		name: spell.name,
		image: spell.image.full
	});
}

const runeById = new Map<number, { key: string; name: string; icon: string }>();
for (const tree of runes) {
	// Add tree icon
	runeById.set(tree.id, { key: tree.key, name: tree.name, icon: tree.icon });
	// Add individual runes
	for (const slot of tree.slots) {
		for (const rune of slot.runes) {
			runeById.set(rune.id, { key: rune.key, name: rune.name, icon: rune.icon });
		}
	}
}

const championByKey = new Map<string, { id: string; name: string; image: string }>();
for (const champ of Object.values(champions.data)) {
	championByKey.set(champ.key, {
		id: champ.id,
		name: champ.name,
		image: champ.image.full
	});
}

/** Every champion by numeric id, sorted by display name (for pickers). */
export const championList = [...championByKey].map(([key, c]) => ({ id: Number(key), name: c.name })).sort((a, b) => a.name.localeCompare(b.name));

const letters = (name: string) => name.toLowerCase().replace(/[^a-z]/g, "");
const championIdByLetters = new Map(championList.map(c => [letters(c.name), c.id]));
/** Display name ("Kai'Sa", "Nunu & Willump") -> champion id; 0 when the bundled data does not know it. */
export const championIdByName = (name: string) => championIdByLetters.get(letters(name)) ?? 0;

const RANK_IMAGES: Record<string, string> = {
	UNRANKED: unrankedImg,
	IRON: ironImg,
	BRONZE: bronzeImg,
	SILVER: silverImg,
	GOLD: goldImg,
	PLATINUM: platinumImg,
	EMERALD: emeraldImg,
	DIAMOND: diamondImg,
	MASTER: masterImg,
	GRANDMASTER: grandmasterImg,
	CHALLENGER: challengerImg
};

export function getRankImage(tier: string): string {
	return RANK_IMAGES[tier.toUpperCase()] ?? unrankedImg;
}

const DEFAULT_TIER_COLOR = { primary: "#6b6b6b", secondary: "#4a4a4a", glow: "rgba(107, 107, 107, 0.4)" };

export function getTierColor(tier: string) {
	return TIER_COLORS[tier.toUpperCase()] ?? DEFAULT_TIER_COLOR;
}

export function isApexTier(tier: string): boolean {
	return ["MASTER", "GRANDMASTER", "CHALLENGER"].includes(tier.toUpperCase());
}

/**
 * Get queue name by queue ID
 */
export function getQueueLabel(queueId: number): string {
	switch (queueId) {
		case 420:
			return "Solo/Duo";
		case 440:
			return "Flex";
		case 400:
			return "Normal Draft";
		case 430:
			return "Normal Blind";
		case 450:
			return "ARAM";
		case 480:
			return "Swiftplay";
		case 490:
			return "Quickplay";
		case 700:
			return "Clash";
		case 710:
			return "Ranked 5s";
		case 720:
			return "ARAM";
		case 830:
		case 840:
		case 850:
			return "Co-op vs AI";
		case 900:
			return "ARURF";
		case 1020:
			return "One for All";
		case 1300:
			return "Nexus Blitz";
		case 1400:
			return "Ultimate Spellbook";
		// custom lobbies: where tournament-code games (Prime League) land
		case 3100:
			return "Custom";
		case 3130:
			return "Custom Draft";
		default:
			return "Normal";
	}
}

/**
 * Get champion icon URL by champion ID
 */
export function getChampionIconUrl(championId: number, gameVersion?: string): string {
	const champ = championByKey.get(String(championId));
	if (champ) {
		const imageKey = champ.image === "FiddleSticks.png" ? "Fiddlesticks.png" : champ.image;
		return `${cdn(gameVersion)}/img/champion/${imageKey}`;
	}
	// Champion newer than the bundled data: neutral placeholder rather than a third-party host
	return PLACEHOLDER_ICON;
}

/** Summoner's Rift minimap (512 px square; covers map coordinates 0-14800 on both axes). */
export const getMapUrl = () => `${DD_BASE}/${DD_VERSION}/img/map/map11.png`;

/** Wide splash art cropped around the champion (unversioned path, proxied through /dd as jpg). */
export function getChampionSplashUrl(championId: number): string {
	const champ = championByKey.get(String(championId));
	return champ ? `${DD_BASE}/img/champion/centered/${champ.id}_0.jpg` : PLACEHOLDER_ICON;
}

/**
 * Get champion name by champion ID
 */
export function getChampionName(championId: number): string {
	const champ = championByKey.get(String(championId));
	return champ?.name ?? "Unknown";
}

/**
 * Get summoner spell icon URL by spell ID
 */
export function getSummonerSpellUrl(spellId: number, gameVersion?: string): string {
	const spell = spellByKey.get(String(spellId));
	if (spell) {
		return `${cdn(gameVersion)}/img/spell/${spell.image}`;
	}
	// Fallback
	return `${cdn(gameVersion)}/img/spell/SummonerFlash.png`;
}

/**
 * Get summoner spell name by spell ID
 */
export function getSummonerSpellName(spellId: number): string {
	const spell = spellByKey.get(String(spellId));
	return spell?.name ?? "Unknown";
}

/**
 * Rune / keystone icon. Data Dragon serves perk images under /cdn/img/ using the paths in runesReforged.json.
 */
export function getRuneIconUrl(perkId: number): string {
	const rune = runeById.get(perkId);
	return rune ? `${DD_BASE}/img/${rune.icon}` : PLACEHOLDER_ICON;
}

/**
 * Get rune/keystone name by perk ID
 */
export function getRuneName(perkId: number): string {
	const rune = runeById.get(perkId);
	return rune?.name ?? "Unknown";
}

/*
 * Get summoner icon URL by profile icon ID
 */
export function getSummonerIconUrl(profileIconId: number): string {
	return `${DD_CDN}/img/profileicon/${profileIconId}.png`;
}

/**
 * Get item icon URL by item ID
 */
export function getItemIconUrl(itemId: number, gameVersion?: string): string {
	return `${cdn(gameVersion)}/img/item/${itemId}.png`;
}
